"use client";
import React, { useMemo, useState } from "react";
import { usePrintingsViewPreference } from "modules/common/stores/useCommonStore";
import { cn, getCardHeroTransitionName } from "utils/utils";
import { GameCard, PrintingsSortEnum } from "modules/cards/model/card";
import { SortOrderEnum } from "modules/collections/details/model/collection";
import { useInfiniteCardPrintings } from "modules/cards/hooks/useCardPrintings";
import { CardSkeleton } from "modules/common/components/CardSkeleton";
import { ResponsiveCardSizing } from "utils/styles";
import type { ViewIndex, ViewName } from "modules/common/model/viewTypes";
import { UNIFIED_VIEWS } from "modules/common/model/viewTypes";
import { ViewSelect } from "modules/common/components/mainbar/ViewSelect";
import { DataTable } from "modules/common/components/Table/DataTable";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { usePrintingsColumns } from "./usePrintingsColumns";
import { ResponsiveCardDisplayGrid } from "modules/common/components/grids/ResponsiveCardDisplayGrid";
import { PrintingGridCard } from "./PrintingGridCard";

interface Props {
  card: GameCard;
  className?: string;
  /**
   * Give grid thumbnails hero view-transition names so navigating to a
   * printing morphs its image into the target page's hero. Page use only —
   * never enable inside the card details dialog (its content stays mounted
   * while hidden and duplicate names abort the transition).
   */
  withHeroTransitions?: boolean;
}

// Module scope, not an inline arrow: ResponsiveCardDisplayGrid's commit effect
// depends on this prop, so a new function per render re-enters that effect on
// every unrelated parent render and can schedule a second startTransition
// while the first commit is still pending.
const printingKey = (c: GameCard) => c.id;

export function CardDetailsPrintings({
  card,
  className,
  withHeroTransitions = false,
}: Props) {
  const [sort, setSort] = useState<PrintingsSortEnum>(
    PrintingsSortEnum.ReleaseDate
  );
  const [order, setOrder] = useState<SortOrderEnum>(SortOrderEnum.Descending);
  const { printingsViewPreference, setPrintingsViewPreference } =
    usePrintingsViewPreference();
  const view: ViewName =
    UNIFIED_VIEWS[printingsViewPreference]?.name ?? "Table";

  const { data, hasNextPage, fetchNextPage, isPending, isFetchingNextPage } =
    useInfiniteCardPrintings(card.id, 20, sort, order);

  const flattenedData = useMemo(
    () => data?.pages.flatMap((p) => p.cards) ?? [],
    [data?.pages]
  );
  const total = data?.pages[0]?.total ?? 0;

  const columns = usePrintingsColumns();
  const table = useReactTable({
    data: flattenedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (card) => card.id,
  });

  return (
    <section
      className={cn("flex flex-col px-2 md:min-h-[35dvh] md:px-0", className)}
      aria-labelledby="printings-heading"
    >
      <header className="mb-1 flex items-center justify-between">
        <h2 className="text-base-content text-lg" id="printings-heading">
          {!isPending && total === 0 ? (
            "No other printings"
          ) : (
            <>
              Other printings
              {!isPending && (
                <span className="badge badge-xs badge-info md:badge-md ml-2">
                  {total}
                </span>
              )}
            </>
          )}
        </h2>
        {(isPending || total > 0) && (
          <ViewSelect
            size="sm"
            options={UNIFIED_VIEWS}
            activeView={UNIFIED_VIEWS[printingsViewPreference]}
            onChange={(value) =>
              setPrintingsViewPreference(Number(value) as ViewIndex)
            }
          />
        )}
      </header>
      {view === "Table" ? (
        <DataTable
          table={table}
          isLoading={isPending}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage ?? false}
          isFetchingNextPage={isFetchingNextPage}
          rowCount={table.getRowModel().rows.length || 10}
          containerClassName="relative max-h-[33vh] overflow-y-auto"
          tableClassName="table-fixed"
          emptyMessage="No other printings"
          emptyDescription=""
        />
      ) : (
        <ResponsiveCardDisplayGrid
          data={flattenedData}
          isLoading={isPending}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage ?? false}
          isFetchingNextPage={isFetchingNextPage}
          getItemKey={printingKey}
          containerClassName="max-h-[33vh]"
          emptyMessage="No other printings"
          emptyDescription=""
          renderItem={(cardPrinting) => (
            <PrintingGridCard
              card={cardPrinting}
              viewTransitionName={
                withHeroTransitions && cardPrinting.id !== card.id
                  ? getCardHeroTransitionName(cardPrinting)
                  : undefined
              }
            />
          )}
          renderSkeleton={() => (
            <div style={ResponsiveCardSizing}>
              <CardSkeleton />
            </div>
          )}
        />
      )}
    </section>
  );
}
